"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { CheckList } from "@/components/tools/research-writing/check-list";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildGapDocument,
  GAP_TYPES,
  type GapInput,
  type GapType,
  gapChecks,
  gapWords,
} from "@/lib/tool-kit/research-writing/research-gap";

/** English strings. A translated content file overrides these through `ui`. */
export const RESEARCH_GAP_STATEMENT_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  typeHeading: "The kind of gap",
  typeLabel: "Gap type",
  gapTypes: {
    evidence: "Evidence — findings conflict",
    knowledge: "Knowledge — something has not been studied",
    practice: "Practice — what is done differs from what research says",
    method: "Method — earlier studies were limited by how they were done",
    empirical: "Empirical — a claim has not been tested",
    theory: "Theory — existing theory does not explain it",
    population: "Population — a group or setting is missing",
  },
  typeHints: {
    evidence:
      "Studies disagree, or a new result runs against widely accepted conclusions.",
    knowledge: "A question has not been asked, or has not been answered.",
    practice:
      "Practitioners do something that research does not support, or research recommends something that is not done.",
    method:
      "Earlier work relied on a design, measure or analysis that limits what it can show.",
    empirical:
      "A theory or proposition exists but has not been tested with data, or not in this context.",
    theory:
      "There is no adequate framework, or the available ones do not fit the phenomenon.",
    population:
      "The studies left out a group, region, age range or setting, so their findings may not apply to it.",
  },
  writeHeading: "Your statements",
  territoryLabel: "What research has established",
  territoryPlaceholder:
    "peer tutoring is associated with higher course grades in first-year courses (Author, year)",
  territoryHint:
    "A clause that finishes “Research has established that …”. Cite it.",
  detailLabels: {
    evidence: "What conflicts",
    knowledge: "What is not known",
    practice: "How practice and research diverge",
    method: "How earlier methods limit them",
    empirical: "What has not been tested",
    theory: "What existing theory fails to explain",
    population: "Who or what is missing",
  },
  detailPlaceholders: {
    evidence:
      "some studies report a gain, others none, with no agreed explanation",
    knowledge:
      "whether tutoring predicts staying enrolled into the second year",
    practice:
      "most programs offer tutoring to everyone, though evidence on targeting is thin",
    method: "short follow-up periods and self-reported attendance",
    empirical: "outside laboratory settings, in real classrooms",
    theory: "why gains persist after tutoring ends",
    population: "first-generation students as a separate group",
  },
  detailHints: {
    evidence: "Finishes “However, findings conflict: …”.",
    knowledge:
      "Start with whether, how, why or what. Finishes “However, it remains unknown …”.",
    practice: "Finishes “However, practice and research diverge: …”.",
    method: "Finishes “However, earlier studies were limited by …”.",
    empirical: "Finishes “However, this claim has not been tested …”.",
    theory: "Finishes “However, existing theory does not explain …”.",
    population: "Finishes “However, these studies did not include …”.",
  },
  consequenceLabel: "Why the gap matters",
  consequencePlaceholder:
    "tutoring budgets are being targeted without evidence of who benefits",
  consequenceHint: "Finishes “This matters because …”.",
  approachLabel: "What your study will do",
  approachPlaceholder:
    "examine whether peer tutoring predicts second-year re-enrollment",
  approachHint:
    "Start with a verb. Finishes “The purpose of this study is to …”.",
  settingLabel: "Setting or sample (optional)",
  settingPlaceholder: "at a large public university",
  searchLabel: "How you checked that the gap exists",
  searchPlaceholder:
    "Databases, search terms, years, and how many records you screened",
  searchHint:
    "Not part of the statement. Keep it for your methods or your notes: a gap claim is only as good as the search behind it.",
  resultsHeading: "Checks",
  statWords: "Words in the statements",
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    territory_done: "The established knowledge is stated and sets up the gap.",
    territory_todo: "State what research has established.",
    detail_done: "The gap is named.",
    detail_todo: "Name the gap.",
    consequence_done: "The gap’s consequence is stated.",
    consequence_todo: "Say why the gap matters.",
    approach_done: "The purpose is stated.",
    approach_todo: "Say what your study will do.",
    search_done: "You have a record of how you checked.",
    search_note:
      "No record of how you checked. A reviewer may ask, so note your databases and search terms.",
    start_done: "The gap starts with a word that fits “it remains unknown …”.",
    start_note:
      "A knowledge gap reads best starting with whether, how, why or what, so “it remains unknown …” makes sense.",
    absolute_done: "No absolute claims such as “no study”.",
    absolute_note:
      "Found: {items}. A claim that nothing exists is disproved by one missed paper. Say “few studies” or document your search.",
  },
  fileName: "research-gap-statement",
  document: {
    heading: "Your gap and purpose statements",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Research Gap Statement",
  subtitle: "Gap statement and purpose statement",
  headings: {
    gap: "Gap statement",
    purpose: "Purpose statement",
    checked: "How the gap was checked",
  },
  tips: {
    gap: "Territory, gap, consequence — in that order, in one paragraph. Swales’ model calls this establishing the territory and then the niche.",
    purpose:
      "Occupy the niche: say what your study does about the gap, in one sentence, with a verb.",
    checked:
      "Keep a record of the search that shows the gap. It belongs in your notes or methods, not in the statement.",
  },
  territoryFrame: "Research has established that {territory}.",
  gapFrames: {
    evidence: "However, findings conflict: {detail}.",
    knowledge: "However, it remains unknown {detail}.",
    practice: "However, practice and research diverge: {detail}.",
    method: "However, earlier studies were limited by {detail}.",
    empirical: "However, this claim has not been tested {detail}.",
    theory: "However, existing theory does not explain {detail}.",
    population:
      "However, these studies did not include {detail}, so it is unclear whether the findings apply to them.",
  },
  consequenceFrame: "This matters because {consequence}.",
  purposeFrame: "The purpose of this study is to {approach}{setting}.",
  prompts: {
    territory:
      "State what is established: “Research has established that [finding] [citation].”",
    gap: {
      evidence:
        "Name the conflict: “However, findings conflict: [study A found …, study B found …].”",
      knowledge:
        "Name what is not known: “However, it remains unknown [whether / how / why …].”",
      practice:
        "Name the divergence: “However, practice and research diverge: [what is done vs what research says].”",
      method:
        "Name the limit: “However, earlier studies were limited by [design, measure or analysis].”",
      empirical:
        "Name what is untested: “However, this claim has not been tested [where or how].”",
      theory:
        "Name what theory misses: “However, existing theory does not explain [phenomenon].”",
      population:
        "Name who is missing: “However, these studies did not include [group or setting].”",
    },
    consequence:
      "Say what the gap prevents: “This matters because [consequence for research, practice or policy].”",
    approach:
      "State the purpose: “The purpose of this study is to [verb] [what] [in what setting].”",
    search:
      "Note the databases, search terms, years and number of records you screened.",
  },
  example: {
    gapType: "population",
    territory:
      "peer tutoring is associated with higher course grades in first-year STEM courses (Author, year)",
    detail: "first-generation students as a separate group",
    consequence:
      "tutoring budgets are being targeted without evidence of who benefits",
    approach:
      "examine whether peer tutoring predicts second-year re-enrollment, and whether the association differs for first-generation students",
    setting: "at a large public university",
    search:
      "Searched [databases] for [terms], [years]; [N] records screened, [N] studies reported results for first-generation students separately.",
  },
} as const;

const EMPTY: GapInput = {
  gapType: "knowledge",
  territory: "",
  detail: "",
  consequence: "",
  approach: "",
  setting: "",
  search: "",
};

type TextKey = Exclude<keyof GapInput, "gapType">;

export default function ResearchGapStatementGeneratorWidget() {
  const strings = useToolUi(RESEARCH_GAP_STATEMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<GapInput>(EMPTY);
  const doc = useMemo(
    () => buildGapDocument(input, strings, locale),
    [input, strings, locale]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const checkText = strings.checkText as Record<string, string>;
  const checks = gapChecks(input).map((check) => ({
    key: check.id,
    state: check.state,
    text: t(checkText[`${check.id}_${check.state}`] ?? "", {
      items: (check.items ?? []).join(", "),
    }),
  }));
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const type = input.gapType;
  const field = (
    key: TextKey,
    id: string,
    label: string,
    placeholder: string,
    hint?: string,
    rows = 2
  ) => (
    <TextAreaField
      hint={hint}
      id={`rg-${id}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({ ...EMPTY, ...strings.example, gapType: "population" })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.typeHeading}>
        <SelectField
          id="rg-type"
          label={strings.typeLabel}
          onChange={(gapType: GapType) =>
            setInput((previous) => ({ ...previous, gapType }))
          }
          options={GAP_TYPES.map((value) => ({
            value,
            label: strings.gapTypes[value],
          }))}
          value={type}
        />
        <p className={HINT}>{strings.typeHints[type]}</p>
      </Panel>

      <Panel title={strings.writeHeading}>
        {field(
          "territory",
          "territory",
          strings.territoryLabel,
          strings.territoryPlaceholder,
          strings.territoryHint
        )}
        {field(
          "detail",
          "detail",
          strings.detailLabels[type],
          strings.detailPlaceholders[type],
          strings.detailHints[type]
        )}
        {field(
          "consequence",
          "consequence",
          strings.consequenceLabel,
          strings.consequencePlaceholder,
          strings.consequenceHint
        )}
        {field(
          "approach",
          "approach",
          strings.approachLabel,
          strings.approachPlaceholder,
          strings.approachHint
        )}
        {field(
          "setting",
          "setting",
          strings.settingLabel,
          strings.settingPlaceholder,
          undefined,
          1
        )}
        {field(
          "search",
          "search",
          strings.searchLabel,
          strings.searchPlaceholder,
          strings.searchHint
        )}
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statWords}
            value={fmt(gapWords(input, strings, locale))}
          />
        </StatGrid>
        <CheckList
          items={checks}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="rg"
        labels={documentLabels}
      />
    </div>
  );
}
